/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnInit} from '@angular/core';
import {safeDetectChanges} from '../common/util/safe-detect-changes';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import * as _ from 'lodash';

import template from './copy-targeting-templates.component.html';
import {TargetingHelper} from './targeting-helper';
import {TeamResource} from '../../components/api/resources/resource/team.resource';
import {TargetingTemplateResource} from '../../components/api/resources/resource/targeting-template.resource';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

@Component({
  standalone: false,
  selector: 'adn-copy-targeting-templates',
  template
})
export class CopyTargetingTemplatesComponent implements OnInit {
  @Input() tabController: any;

  targetingTemplateList: any = {items: []};
  allTeams: any[];
  yourTeams: any[] = [];
  teams: any[];

  /** Buffered for template — avoids NG0100 when list-helper fills items mid-refresh. */
  listReady = false;
  hasItems = false;
  displayItems: any[] = [];
  listPageSize = 25;
  listTotalCount = 0;
  listCurrentPage = 1;

  constructor(
    private readonly adnListHelper: AdnListHelperService,
    @Inject(TargetingTemplateResource) private readonly TargetingTemplate: any,
    @Inject(TeamResource) private readonly Team: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.adnListHelper.setUpBasicList(
      this.targetingTemplateList,
      this.TargetingTemplate,
      'targetingTargetingTemplates',
      {
        filterBy: 'type',
        filterByLike: 'TARGETING',
        afterFill: () => {
          deferMarkForCheck(this.cdr, () => {
            // Snapshot — do not bind targetingTemplateList.items (list-helper
            // replaces that array on settle and can race checkNoChanges).
            this.displayItems = (this.targetingTemplateList.items || []).slice();
            this.hasItems = this.displayItems.length > 0;
            this.listPageSize = this.targetingTemplateList.pageSize || 25;
            this.listTotalCount = this.targetingTemplateList.totalCount || 0;
            this.listCurrentPage = this.targetingTemplateList.currentPage || 1;
            this.listReady = true;
          });
        }
      }
    );
  }

  editInvoked(template: any): void {
    template.editable = true;
    template.teams = _.map(template.teams, (team) => team.id || team);
    this.teamQuery(template);
  }

  editCancelled(template: any): void {
    template.editable = false;
  }

  editTemplate(template: any): void {
    const templateForSave = _.cloneDeep(template);
    const nt = new this.TargetingTemplate(templateForSave);

    if (!nt.teamType || nt.teamType === 'YOUR') {
      nt.teams = this.yourTeams;
    }

    nt.savePartial().then(() => {
      this.ngZone.run(() => {
        this.reloadList();
        safeDetectChanges(this.cdr);
      });
    });
  }

  deleteInvoked(template: any): void {
    template.$archive();
    setTimeout(() => {
      this.ngZone.run(() => {
        this.reloadList();
        safeDetectChanges(this.cdr);
      });
    }, 150);
  }

  copyTemplateInvoked(nt: any): void {
    this.tabController.copyTargetingTemplate(
      TargetingHelper.getTargetingTypeFromTemplate(nt.type).widget,
      nt
    );
    nt.copy = true;
  }

  changePage(page: number): void {
    if (!this.targetingTemplateList || page < 1) {
      return;
    }
    this.targetingTemplateList.currentPage = page;
    this.targetingTemplateList.changePage();
  }

  pageStart(): number {
    return this.listPageSize * (this.listCurrentPage - 1) + 1;
  }

  pageEnd(): number {
    return Math.min(this.listPageSize * this.listCurrentPage, this.listTotalCount);
  }

  private reloadList(): void {
    this.listReady = false;
    this.hasItems = false;
    this.displayItems = [];
    this.listPageSize = 25;
    this.listTotalCount = 0;
    this.listCurrentPage = 1;
    this.targetingTemplateList.makeQuery();
  }

  private teamQuery(template: any): void {
    const doType = () => {
      template.teamType = _.get(this, ['yourTeams', 'length']) === _.get(template, ['teams', 'length'])
        ? 'YOUR'
        : 'CUSTOM';
    };

    if (this.allTeams) {
      doType();
      return;
    }

    this.Team.query({minimal: true}).$promise.then((page: any) => {
      this.ngZone.run(() => {
        this.allTeams = page.results;
        const isViewAllObjects = this.LocalUserPermissions.hasNetwork('VIEW_ALL_OBJECTS');
        this.yourTeams = _.filter(this.allTeams, (g) => {
          return this.LocalUserPermissions.isPartOf(g.network.tagId || g.network.id, g.id);
        });
        this.teams = isViewAllObjects ? this.allTeams : this.yourTeams;
        doType();
        safeDetectChanges(this.cdr);
      });
    });
  }
}
